import { NEW_ARRIVALS_SUBCATEGORY_URL_ID } from "@faire/retailer-shared/categories/urlId";
import { ICategory } from "@faire/web-api/indigofair/data/ICategory";
import { ILayoutElement } from "@faire/web-api/indigofair/data/ILayoutElement";
import { ISearchRequestReferrer } from "@faire/web-api/indigofair/data/ISearchRequestReferrer";
import { SearchMakersWithFiltersSortBy } from "@faire/web-api/indigofair/data/SearchMakersWithFiltersSortBy";
import { SortOrder } from "@faire/web-api/indigofair/data/SortOrder";
import { IRoutingContext } from "@faire/web/ui/routing/RoutingContext";

import { localizedUrlWithQueryParams } from "@faire/retailer/lib/pathUtils";
import { IUrlId } from "@faire/retailer/models/Category/IUrlId";
import { QueryParameters } from "@faire/retailer/paths/category/QueryParameters";
import { CategoryPagePaths } from "@faire/retailer/paths/CategoryPage";
import { useRetailerRoutingContext } from "@faire/retailer/RetailerRoutingContextProvider";
import { useCategoryStore } from "@faire/retailer/stores/domain/CategoryStore";
import { findClosestActiveCategoryUrlIdsByPathIdentifier } from "@faire/retailer/stores/domain/CategoryStore/findByPathIdentifier";
import { findActiveCategoryByUrlIds } from "@faire/retailer/stores/domain/CategoryStore/findByUrlId";

const buildCategoryPath = (
  {
    category,
    sort_order,
    sort_by,
    filter_keys,
  }: {
    category: string | undefined;
    filter_keys: Array<string>;
    sort_order?: keyof typeof SortOrder | undefined;
    sort_by?: keyof typeof SearchMakersWithFiltersSortBy | undefined;
  },
  categories: ICategory[] | undefined,
  routingContext: IRoutingContext
): string | undefined => {
  const queryParams: QueryParameters = {};
  if (category === undefined) {
    return undefined;
  }

  const categoryUrlIds = findClosestActiveCategoryUrlIdsByPathIdentifier(
    category,
    categories
  ) ?? [{ id: category } as IUrlId];

  if (
    sort_by === SearchMakersWithFiltersSortBy.FIRST_ACTIVE_AT &&
    sort_order === SortOrder.DESC && // Show New arrivals for C1 or C2
    findActiveCategoryByUrlIds(
      [
        ...categoryUrlIds.map((urlId) => urlId.id),
        NEW_ARRIVALS_SUBCATEGORY_URL_ID,
      ],
      categories
    )
  ) {
    // If filters values have specific values (first_active_at) + sort descending
    // route to new arrivals subcategory if it exists
    categoryUrlIds.push({ id: NEW_ARRIVALS_SUBCATEGORY_URL_ID });
  } else {
    if (sort_by) {
      queryParams.sort_by = sort_by;
    }
    if (sort_order) {
      queryParams.sort_order = sort_order;
    }
  }

  if (filter_keys !== undefined && filter_keys.length > 0) {
    queryParams.filters = filter_keys.join(",");
  }

  return CategoryPagePaths.index({
    categoryUrlIds,
    queryParams,
    routingContext,
  });
};

export const HOMEPAGE_RECENTLY_SEARCHED_QUERY_PRODUCTS_CAROUSEL_EVENT_NAME =
  "homepage_recently_searched_query_products";
/**
 * Used to link to category page from dynamic Category Carousels and other dynamic links
 */
export const useLayoutElementCarouselCtaLink = (
  layoutElement: ILayoutElement,
  requestId: string
): string | undefined => {
  const { categories } = useCategoryStore();
  const routingContext = useRetailerRoutingContext();
  const {
    cta_link_to_search_products,
    cta_link_to_search_makers,
    cta_link,
    on_click_event_name,
  } = layoutElement;
  const newLocal =
    cta_link &&
    on_click_event_name ===
      HOMEPAGE_RECENTLY_SEARCHED_QUERY_PRODUCTS_CAROUSEL_EVENT_NAME;
  if (newLocal) {
    return localizedUrlWithQueryParams(cta_link, {
      refType:
        ISearchRequestReferrer.SearchRequestReferrerType
          .HOMEPAGE_NEW_PRODUCTS_FROM_RECENT_SEARCH,
      refReqId: requestId,
    });
  } else if (cta_link_to_search_products) {
    return buildCategoryPath(
      cta_link_to_search_products,
      categories,
      routingContext
    );
  } else if (cta_link_to_search_makers) {
    return buildCategoryPath(
      cta_link_to_search_makers,
      categories,
      routingContext
    );
  } else {
    return cta_link;
  }
};
